import { alpha, Box, Typography } from "@mui/material";
import { Moment } from "moment";
import { useEffect, useState } from "react";
import { Task } from "../../../api/Tasks";
import { isToday } from "../../../misc/dates";
import { DragTask } from "./range";

// CalendarCollapsedDay is a day squeezed to a thin bar. It only says how many tasks
// the day holds, and a click opens the view that has room for them.
export function CalendarCollapsedDay(props: {
    date: Moment;
    count: number;
    onClick: () => void;

    // a task dragged in from another day is moved to this one
    dateKey: string;
    dragging: DragTask | null;
    onDragEnd: () => void;
    onReschedule: (task: Task, date: string) => void;
}) {
    const today = isToday(props.date);
    const [dropping, setDropping] = useState(false);

    const incoming = !!props.dragging && props.dragging.date !== props.dateKey;
    useEffect(() => {
        if (!props.dragging) setDropping(false);
    }, [props.dragging]);

    return (
        <Box
            role="button"
            onClick={props.onClick}
            onDragOver={e => {
                if (!incoming) return;

                e.preventDefault();
                setDropping(true);
            }}
            onDragLeave={() => setDropping(false)}
            onDrop={e => {
                setDropping(false);
                if (!incoming || !props.dragging) return;

                e.preventDefault();
                props.onReschedule(props.dragging.task, props.dateKey);
                props.onDragEnd();
            }}
            title={(props.count > 0 ? props.count + (props.count === 1 ? " task on " : " tasks on ") : "")
                + props.date.format("dddd, MMM D") + "\nClick to show the full week"}
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.5,
                minWidth: 0,
                py: 0.5,
                borderRight: 1,
                borderBottom: 1,
                borderColor: "grey.300",
                bgcolor: theme => dropping ? alpha(theme.palette.primary.main, 0.16) : theme.palette.grey[100],
                cursor: "pointer",
                "&:hover": { bgcolor: dropping ? undefined : "grey.200" },
            }}
        >
            <Typography variant="caption" sx={{
                fontSize: 10,
                fontWeight: today ? 700 : 600,
                color: today ? "primary.main" : "text.disabled",
            }}>
                {props.date.date()}
            </Typography>

            {props.count > 0 && (
                <Typography variant="caption" sx={{
                    minWidth: 16,
                    px: 0.25,
                    borderRadius: 10,
                    fontSize: 10,
                    fontWeight: 700,
                    lineHeight: "16px",
                    textAlign: "center",
                    color: "common.white",
                    bgcolor: "text.secondary",
                }}>
                    {props.count}
                </Typography>
            )}
        </Box>
    );
}
